/* LEGEND — Trend Lines: on-chart render layer (1-minute chart).
   A canvas glued to the ChartPane's time & price axes. Draws the classic
   rulers from lib/trendlines: resistance from the high of the day across the
   bounce tops, support from the low of the day under the landings —
   with anchor markers (H1/H2/L1/L2), kept history (broken / superseded lines
   faded + dashed, ending where they ended), break × marks, a right-edge value
   label per active line and a corner status readout. Drawing only —
   pointer-events: none, no orders, no signals, nothing wired into entry, exit,
   stop or sizing logic. Ported from the prototype's legend/trendlinelayer.jsx;
   the geometry follows the plan, not the prototype.

   DATA: the layer feeds the engine the candles the chart is ALREADY drawing,
   straight off chartApiRef.getCandles() (ChartPane's live array). All bars but
   the last are completed and are pushed once each; the last is the forming bar
   and is only probed for wick violations. No second fetch, no second bar
   subscription, and the lines can never disagree with the bars under them.
   Every timeframe is drawn; intraday TFs are day-scoped, 1H+ series-scoped.
   Only the LATEST ruler per side is on the chart (the current one, or the one
   before it while a new bounce is awaited) — the engine keeps the rest as data.

   X COORDINATE: lightweight-charts spaces bars by index, so a straight pixel
   line between the two anchor bars, extrapolated in pixel space, IS bar-index
   geometry — it stays straight across missing minutes in thin premarket tape.

   PERFORMANCE CONTRACT (VisionLayer / #586 rules, enforced here):
   - The engine is incremental: one push per completed bar, one O(1) probe per
     tick for the forming bar. A quote flood costs nothing extra.
   - The rAF loop is a paint governor: a dirty key (engine version, size/DPR,
     visible range, two priceToY probes, forming-bar time) skips the paint
     when nothing moved.
   - React state carries only the readout summary (a short string); the frame
     lives in a ref, so a repaint never re-renders the Terminal tree.
   - document.hidden pauses everything. */
import React from 'react';
import { subscribeQuotes } from '../data/source';
import { PullbackTrendEngine, getTrendParams, trendSummaryKey, TL_COLORS, type TlFrame, type TlLine } from '../lib/trendlines';
import { TF_MIN } from '../lib/constants';

const { useRef: useRefTL, useEffect: useEffectTL, useState: useStateTL } = React;
const h = React.createElement;

const STYLE: Record<string, { alpha: number; width: number; dash: number[] }> = {
  active: { alpha: 1, width: 1.9, dash: [] },
  current_broken: { alpha: 0.85, width: 1.7, dash: [] }, // the latest line, crossed — still THE line until a new swing confirms
  broken: { alpha: 0.42, width: 1.3, dash: [5, 4] },
  superseded: { alpha: 0.34, width: 1.3, dash: [2, 4] },
};

function hexA(hex: string, a: number) {
  const s = hex.replace('#', '');
  return 'rgba(' + parseInt(s.slice(0, 2), 16) + ',' + parseInt(s.slice(2, 4), 16) + ',' + parseInt(s.slice(4, 6), 16) + ',' + a + ')';
}

function roundR(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, hh: number, r: number) {
  r = Math.min(r, w / 2, hh / 2);
  ctx.beginPath();
  if ((ctx as any).roundRect) { (ctx as any).roundRect(x, y, w, hh, r); return; }
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + hh, r); ctx.arcTo(x + w, y + hh, x, y + hh, r); ctx.arcTo(x, y + hh, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

const fmtPx = (p: number) => (p < 1 ? p.toFixed(4) : p.toFixed(2));

export function drawTrendFrame(ctx: CanvasRenderingContext2D, w: number, hh: number, coord: { X: (t: number) => number | null; Y: (p: number) => number | null; rightX: number }, fr: TlFrame) {
  if (!fr || !fr.display.length) return;
  const { X, Y, rightX } = coord;

  // only the latest ruler per side (the engine keeps the rest as data, not drawing)
  const ordered = fr.display.slice().sort((a, b) => (a.status === 'active' ? 1 : 0) - (b.status === 'active' ? 1 : 0));
  for (const ln of ordered) {
    const col = ln.kind === 'upper' ? TL_COLORS.upper : TL_COLORS.lower;
    const s = STYLE[ln.status] || STYLE.active;
    const x1 = X(ln.t1), x2 = X(ln.t2), y1 = Y(ln.p1), y2 = Y(ln.p2);
    if (x1 == null || x2 == null || y1 == null || y2 == null || x2 === x1) continue;
    // pixel slope between the anchor BARS = bar-index geometry; every line is
    // extrapolated to the right edge — a broken line stays on the chart (solid
    // to the crossing, dashed after it), that crossing being the whole point
    const m = (y2 - y1) / (x2 - x1);
    const xEnd = rightX;
    const yEnd = y1 + m * (xEnd - x1);
    const isCurrent = ln.id === fr.upperLineId || ln.id === fr.lowerLineId;
    const st = isCurrent ? (ln.status === 'broken' ? STYLE.current_broken : STYLE.active) : s;

    ctx.globalAlpha = st.alpha; ctx.strokeStyle = col; ctx.lineWidth = st.width;
    if (ln.brokenTime != null) {
      const bxr = X(ln.brokenTime), bxc = bxr == null ? xEnd : Math.max(x1, Math.min(xEnd, bxr));
      const yb = y1 + m * (bxc - x1);
      ctx.setLineDash(isCurrent ? [] : st.dash); ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(bxc, yb); ctx.stroke();
      ctx.globalAlpha = st.alpha * 0.6; ctx.setLineDash([5, 4]); ctx.beginPath(); ctx.moveTo(bxc, yb); ctx.lineTo(xEnd, yEnd); ctx.stroke();
      ctx.globalAlpha = st.alpha; ctx.setLineDash([]);
    } else {
      ctx.setLineDash(ln.status === 'active' ? [] : st.dash);
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(xEnd, yEnd); ctx.stroke();
      ctx.setLineDash([]);
    }

    // break × on the line at the violation bar
    if (ln.brokenTime != null && ln.brokenPrice != null) {
      const bx = X(ln.brokenTime), by = Y(ln.brokenPrice);
      if (bx != null && by != null) {
        ctx.globalAlpha = Math.min(0.85, s.alpha + 0.35); ctx.strokeStyle = col; ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.moveTo(bx - 4, by - 4); ctx.lineTo(bx + 4, by + 4); ctx.moveTo(bx + 4, by - 4); ctx.lineTo(bx - 4, by + 4); ctx.stroke();
      }
    }

    // right-edge label with the line's CURRENT value (a falling line's price moves every bar)
    if (isCurrent) {
      const now = ln.kind === 'upper' ? fr.upper : fr.lower;
      const lab = (ln.kind === 'upper' ? 'RESISTANCE ' : 'SUPPORT ') + (now ? fmtPx(now.valueNow) : '') + (ln.status === 'broken' ? ' ×' : '');
      ctx.globalAlpha = 1; ctx.font = '700 8.5px "IBM Plex Sans", sans-serif';
      const tw = ctx.measureText(lab).width, padX = 5, cw = tw + padX * 2, ch = 14;
      const cx = rightX - cw - 2, cy = Math.max(2, Math.min(hh - ch - 2, yEnd - ch / 2));
      ctx.fillStyle = hexA(col, 0.16); roundR(ctx, cx, cy, cw, ch, 4); ctx.fill();
      ctx.fillStyle = col; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
      ctx.fillText(lab, cx + padX, cy + ch / 2 + 0.5);
    }
  }

  // anchor markers (H1/H2/L1/L2) on the CURRENT lines — small hollow squares + label
  ctx.globalAlpha = 1;
  for (const a of fr.anchors) {
    const ax = X(a.t), ay = Y(a.price);
    if (ax == null || ay == null) continue;
    const col = a.kind === 'upper' ? TL_COLORS.upper : TL_COLORS.lower;
    ctx.fillStyle = '#0a0b0d'; ctx.strokeStyle = col; ctx.lineWidth = 1.5;
    roundR(ctx, ax - 3.5, ay - 3.5, 7, 7, 1.5); ctx.fill(); ctx.stroke();
    ctx.font = '700 8.5px "IBM Plex Mono", monospace'; ctx.fillStyle = col;
    ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
    ctx.fillText(a.label, ax, ay + (a.kind === 'upper' ? -11 : 12));
  }
  ctx.globalAlpha = 1; ctx.setLineDash([]);
}

type Feed = { arr: any[] | null; completed: number; lastTime: number; version: number; formingTime: number };

export function TrendLineLayer(props: any) {
  const { chartApiRef, symbol, tf } = props;
  const cvRef = useRefTL<any>(null);
  const engineRef = useRefTL<PullbackTrendEngine | null>(null);
  const feedRef = useRefTL<Feed>({ arr: null, completed: 0, lastTime: 0, version: -1, formingTime: 0 });
  const frameRef = useRefTL<TlFrame | null>(null);
  const spreadRef = useRefTL<number>(0);
  const [summary, setSummary] = useStateTL<string>('warming');
  const stRef = useRefTL<any>({});
  stRef.current = { symbol, tf, summary };
  const perfRef = useRefTL<any>({ paints: 0, skips: 0, lastKey: '' });
  // Symbol/tf switch: fresh engine (params re-read), drop the previous frame
  // immediately — never draw the old symbol's lines over the new bars.
  // Intraday timeframes are one session per ET date; 1H and up treat the
  // whole loaded series as one session (a day-scoped ruler on hourly bars
  // would have a handful of bars to work with).
  useEffectTL(() => {
    const params = getTrendParams();
    params.sessionScoped = (TF_MIN[tf] || 1) < 60;
    engineRef.current = new PullbackTrendEngine(params);
    feedRef.current = { arr: null, completed: 0, lastTime: 0, version: -1, formingTime: 0 };
    frameRef.current = null;
    perfRef.current.lastKey = '';
    setSummary('warming');
  }, [symbol, tf]);

  // NBBO spread → tolerance floor. Ref only; the engine reads it on each push.
  useEffectTL(() => {
    spreadRef.current = 0;
    const off = subscribeQuotes(symbol, (q: any) => {
      const b = Number(q && q.bid), a = Number(q && q.ask);
      spreadRef.current = Number.isFinite(a) && Number.isFinite(b) && a > b ? a - b : 0;
    });
    return () => { off(); };
  }, [symbol]);

  useEffectTL(() => {
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (typeof document !== 'undefined' && document.hidden) return; // hidden tab: zero work
      const cv = cvRef.current, api = chartApiRef.current, eng = engineRef.current;
      if (!cv || !api || !api.chart || !eng) return;
      const P = perfRef.current, F = feedRef.current;

      // ---- feed: completed bars pushed once, forming bar probed ----
      const src = api.getCandles ? api.getCandles() : null;
      const arr: any[] = src && src.symbol === stRef.current.symbol && src.tf === stRef.current.tf ? src.candles : [];
      const n = arr.length;
      if (arr !== F.arr || n - 1 < F.completed || (F.completed > 0 && (!arr[F.completed - 1] || arr[F.completed - 1].time !== F.lastTime))) {
        // the pane swapped or rewound its array (hydrate / symbol change): replay from the start
        eng.reset(); F.arr = arr; F.completed = 0; F.lastTime = 0;
      }
      eng.setSpread(spreadRef.current);
      while (F.completed < n - 1) { eng.push(arr[F.completed]); F.completed++; F.lastTime = arr[F.completed - 1].time; }
      const forming = n ? arr[n - 1] : null;
      if (forming) eng.probe(forming);
      const formingTime = forming ? forming.time : 0;
      if (eng.version !== F.version || formingTime !== F.formingTime) {
        F.version = eng.version; F.formingTime = formingTime;
        frameRef.current = n ? eng.frame() : null;
        const sk = trendSummaryKey(frameRef.current);
        if (sk !== stRef.current.summary) { stRef.current.summary = sk; setSummary(sk); }
      }
      const fr = frameRef.current;

      // ---- dirty key: cheap probes; unchanged key ⇒ skip the whole paint ----
      const host = cv.parentElement, w = host.clientWidth, hh = host.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      let key: string;
      if (!fr || !fr.display.length) {
        key = 'empty:' + w + 'x' + hh;
      } else {
        let range: any = null;
        try { range = api.chart.timeScale().getVisibleLogicalRange(); } catch { range = null; }
        // price-scale probe: line Y coords move on autoscale even when the frame
        // and range do not — probe two line extremes through priceToY.
        const l0 = fr.display[0] as TlLine, l1 = fr.display[fr.display.length - 1] as TlLine;
        const y0 = api.priceToY(l0.p1), y1 = api.priceToY(l1.p2);
        key = F.version + ':' + F.formingTime + '|' + w + 'x' + hh + ':' + dpr +
          '|' + (range ? range.from.toFixed(1) + ':' + range.to.toFixed(1) : 'n') +
          '|' + (y0 == null ? 'n' : Math.round(y0)) + ':' + (y1 == null ? 'n' : Math.round(y1));
      }
      if (key === P.lastKey) { P.skips++; return; }
      P.lastKey = key;

      // ---- paint ----
      if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(hh * dpr)) {
        cv.width = Math.round(w * dpr); cv.height = Math.round(hh * dpr);
        cv.style.width = w + 'px'; cv.style.height = hh + 'px';
      }
      const ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, hh);
      if (fr && fr.display.length) {
        const ts = api.chart.timeScale();
        const coord = {
          X: (t: number) => { const c = ts.timeToCoordinate(t); return c == null ? null : c; },
          Y: (p: number) => api.priceToY(p),
          rightX: w - 64, // clear of the price axis, same margin as the vision layer
        };
        try { drawTrendFrame(ctx, w, hh, coord, fr); } catch { /* one bad frame ≠ dead layer */ }
      }
      P.paints++;
      (window as any).__trendPerf = { paints: P.paints, skips: P.skips, version: F.version };
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ---- corner status readout (driven by the summary string only) ----
  const fr = frameRef.current;
  const chip = (k: string, v: string, vcls?: string) => h(React.Fragment, { key: k },
    h('span', { className: 'ts-k' }, k),
    h('span', { className: 'ts-v ' + (vcls || '') }, v));
  const parts: any[] = [];
  let warn = false;
  if (!fr || !fr.ok) { parts.push(h('span', { key: 'warm', className: 'ts-v warn' }, 'warming' + (fr ? ' ' + fr.n + '/' + (2 * fr.N + 1) : ''))); warn = true; }
  else {
    if (fr.upper) {
      const u = fr.upper;
      parts.push(chip('res', (u.crossed ? 'broken ' : 'falling · ') + fmtPx(u.valueNow) + ' · ' + u.touches + 't' + (u.closedAbove ? ' · closed above' : ''), u.crossed ? 'up' : 'warn'));
    } else parts.push(chip('res', fr.h1 ? 'HOD ' + fmtPx(fr.h1.price) + ' · waiting for a bounce' : 'no high yet', 'muted'));
    if (fr.lower) {
      const l = fr.lower;
      parts.push(chip('sup', (l.broken ? 'broken ' : 'rising · ') + fmtPx(l.valueNow) + ' · ' + l.touches + 't', l.broken ? 'muted' : 'up'));
    } else parts.push(chip('sup', fr.l1 ? 'LOD ' + fmtPx(fr.l1.price) + ' · waiting for a landing' : 'none', 'muted'));
    if (fr.squeeze) parts.push(h('span', { key: 'sq', className: 'ts-v squeeze' }, 'squeeze'));
  }

  return h('div', { className: 'tl-layer' },
    h('canvas', { className: 'tl-canvas', ref: cvRef }),
    h('div', {
      className: 'tl-status' + (warn ? ' warn' : ''),
      title: 'Trend lines — drawing only. Intraday: one session per ET day (04:00–20:00); 1H and up: the whole series. Resistance from the high across the bounce tops, support from the low under the landings; swings confirmed N=' + (fr ? fr.N : getTrendParams().swingBars) + ' bars later; a break is marked with an ×, the ruler kept until the next bounce; "3t" = three touches. Only the latest ruler per side is drawn. Tune via localStorage["legend.trend.params"].',
    },
      h('span', { className: 'ts-badge' }, h('span', { className: 'd' }), h('span', null, 'Trend')),
      h('span', { className: 'ts-sep' }),
      parts));
}
